import NetworkFactory from '@/drivers/network'
import SocketFactory from '@/drivers/socket'
import PrinterQueue from '@/integrations/printer/printerQueue'
import { printerTest as ticketPrinterTest } from '@/tickets/printerTest'

import EscPos from './printers/escpos'
import UsbPrinter from './printers/usb'
import UsbAndroid from './printers/usb-android'
import type { DiscoveredPrinter, Printer } from './types'

export async function discoverPrinters(existingPrinters: Printer[]) {
  const androidUsbPrinters = await getAndroidPrinters()
  const printerIps = await getPrintersOnNetwork(
    existingPrinters.filter(printer => printer.type !== 'Usb')
  )
  const lanPrinters = Object.values(await getPrintersFromIps(printerIps))
  const usbPrinters = await getWindowsPrinters(
    existingPrinters.filter(printer => printer.type === 'Usb')
  )
  const printers = [...androidUsbPrinters, ...lanPrinters, ...usbPrinters]
  return printers
}

async function getPrintersOnNetwork(existingPrinters: Printer[]) {
  try {
    const network = NetworkFactory.getNetwork()
    const hosts = [network.getSubnetPrefix()].flatMap(subnet =>
      Array(256)
        .fill('')
        .map((_, index) => `${subnet}${index}`)
        .filter(ipAddress => !existingPrinters.some(printer => printer.ip == ipAddress))
    )

    const res = hosts.map(host => {
      return new Promise<string | null>(resolve => {
        const socket = SocketFactory.getSocket()
        socket.open(
          host,
          9100,
          () => {
            clearTimeout(to)
            socket.close()
            resolve(host)
          },
          () => {
            clearTimeout(to)
            socket.close()
            resolve(null)
          }
        )
        const to = setTimeout(() => {
          socket.close()
          resolve(null)
        }, 2000)
      })
    })

    return (await Promise.all(res)).filter((p): p is string => p !== null)
  } catch {
    return []
  }
}

async function getPrintersFromIps(printerIps: string[]) {
  let printerId = 1
  const res: Record<string, DiscoveredPrinter> = {}
  for (const printerIp of printerIps) {
    for (const type of ['EscPos']) {
      try {
        if (type === 'EscPos') {
          // @ts-expect-error Minimum info for EscPos printer
          const p = new EscPos({
            type,
            address: printerIp
          })
          const status = (await p.getStatus())?.toString().charCodeAt(0)

          if (status) {
            res[printerIp] = {
              name: `Printer ${printerId}`,
              type,
              ip: printerIp
            }
            printerId++
          }
        }

        if (printerIp in res) {
          await printTest({ ...res[printerIp] })
        }
      } catch {
        continue
      }
    }
  }

  return res
}

async function getWindowsPrinters(existingPrinters: Printer[]) {
  try {
    const usbPrinters = await UsbPrinter.discoverPrinters()
    const filteredPrinters: DiscoveredPrinter[] = usbPrinters
      .filter(printer => {
        if (existingPrinters.some(ePrinter => ePrinter.name === printer.name)) {
          return false
        } else if (printer.portName) {
          const offline = printer.attributes && printer.attributes.includes('OFFLINE')
          return !offline
        } else if (printer.options) {
          return printer.options['device-uri'].startsWith('usb://')
        } else {
          return false
        }
      })
      .map(printer => ({
        name: printer.name,
        type: 'Usb'
      }))

    const printersWorking: DiscoveredPrinter[] = []

    for (const printer of filteredPrinters) {
      try {
        await printTest(printer)
        printersWorking.push(printer)
      } catch {
        continue
      }
    }

    return printersWorking
  } catch {
    return []
  }
}

async function getAndroidPrinters() {
  try {
    const printers = await UsbAndroid.discoverPrinters()
    const discoveredPrinters = printers.map(printer => ({
      type: 'UsbAndroid' as const,
      name: printer.name
    }))

    for (const printer of discoveredPrinters) {
      try {
        await printTest(printer)
      } catch {
        continue
      }
    }

    return discoveredPrinters
  } catch {
    return []
  }
}

async function printTest(printer: DiscoveredPrinter) {
  // @ts-expect-error Minimum info for EscPos printer
  const printerQueue = new PrinterQueue(printer)

  const ticket = ticketPrinterTest(printer)
  const imageData = ticket.toDataURL('image/png')

  const job = {
    type: 'printImage',
    image: imageData,
    time: new Date()
  } as const

  printerQueue.addJob(job)
}
